/**
 * Executive Snapshot
 * 
 * The first thing executive users see every morning.
 * A complete picture of organizational operational health.
 * 
 * Answers: "Is our organization ready to operate today?"
 */
import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import {
  Camera, RefreshCw, Users, Heart, Calendar, DollarSign, Receipt,
  AlertTriangle, Shield, FileText, CheckCircle, XCircle, Loader2,
  Clock, TrendingUp, Building, ArrowRight, Activity, Briefcase
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Link } from "react-router-dom";

// Metric card component with enterprise styling
const MetricCard = ({ icon: Icon, label, value, subtext, status, onClick }) => {
  const statusStyles = {
    good: "border-l-4 border-l-emerald-500",
    warning: "border-l-4 border-l-amber-500",
    alert: "border-l-4 border-l-red-500",
    neutral: "",
  };

  return (
    <div 
      className={`bg-white rounded border border-[#E2E8F0] p-5 hover:shadow-md transition-all duration-200 ${statusStyles[status] || ""} ${onClick ? "cursor-pointer hover:-translate-y-0.5" : ""}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="w-10 h-10 bg-[#F1F5F9] rounded flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#0A192F]" />
        </div>
        {status && status !== "neutral" && (
          <Badge 
            className={
              status === "good" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : 
              status === "warning" ? "bg-amber-50 text-amber-700 border-amber-200" : 
              "bg-red-50 text-red-700 border-red-200"
            }
          >
            {status === "good" ? "Ready" : status === "warning" ? "Review" : "Action Required"}
          </Badge>
        )}
      </div>
      <p className="text-3xl font-bold text-[#0A192F]">{value}</p>
      <p className="text-sm font-medium text-[#475569] mt-1">{label}</p>
      {subtext && <p className="text-xs text-[#475569]/70 mt-1">{subtext}</p>}
    </div>
  );
};

// Readiness indicator
const ReadinessIndicator = ({ ready, label }) => (
  <div className="bg-white rounded border border-[#E2E8F0] p-5">
    <div className="flex items-center gap-4">
      {ready ? (
        <div className="w-10 h-10 bg-emerald-50 rounded flex items-center justify-center">
          <CheckCircle className="w-5 h-5 text-emerald-600" />
        </div>
      ) : (
        <div className="w-10 h-10 bg-amber-50 rounded flex items-center justify-center">
          <XCircle className="w-5 h-5 text-amber-600" />
        </div>
      )}
      <div>
        <p className={`text-xl font-bold ${ready ? "text-emerald-600" : "text-amber-600"}`}>
          {ready ? "Ready" : "Pending"}
        </p>
        <p className="text-sm text-[#475569]">{label}</p>
      </div>
    </div>
  </div>
);

// Quick action card
const QuickAction = ({ icon: Icon, label, href, description }) => (
  <Link 
    to={href}
    className="flex items-center gap-4 p-4 bg-white rounded border border-[#E2E8F0] hover:border-[#D4C19C]/50 hover:shadow-sm transition-all duration-200 group"
  >
    <div className="w-10 h-10 bg-[#F1F5F9] rounded flex items-center justify-center group-hover:bg-[#0A192F] transition-colors">
      <Icon className="w-5 h-5 text-[#0A192F] group-hover:text-white transition-colors" />
    </div>
    <div className="flex-1">
      <p className="font-medium text-[#0A192F]">{label}</p>
      <p className="text-xs text-[#475569]">{description}</p>
    </div>
    <ArrowRight className="w-4 h-4 text-[#475569] group-hover:text-[#D4C19C] transition-colors" />
  </Link>
);

export default function ExecutiveSnapshot() {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    loadSnapshot();
  }, []);

  const loadSnapshot = async () => {
    setLoading(true);
    try {
      const res = await api.get("/snapshot/current");
      setSnapshot(res.data);
    } catch (err) {
      console.error("Failed to load snapshot:", err);
      toast.error("Failed to load Executive Snapshot");
    } finally {
      setLoading(false);
    }
  };

  const generateSnapshot = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/snapshot/generate");
      setSnapshot(res.data);
      toast.success("Executive Snapshot updated");
    } catch (err) {
      toast.error("Failed to generate snapshot");
    } finally {
      setGenerating(false);
    }
  };

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#0A192F] mx-auto mb-4" />
          <p className="text-[#475569]">Loading Executive Snapshot...</p>
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        <div className="text-center py-20 bg-white rounded border border-[#E2E8F0]">
          <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mx-auto mb-4">
            <Camera className="w-8 h-8 text-[#475569]" />
          </div>
          <h3 className="text-xl font-semibold text-[#0A192F] mb-2">No Executive Snapshot Available</h3>
          <p className="text-[#475569] mb-6">Generate your first operational snapshot</p>
          <Button 
            onClick={generateSnapshot} 
            className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white rounded"
            data-testid="generate-snapshot-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Generate Snapshot
          </Button>
        </div>
      </div>
    );
  }

  const readyPercent = snapshot.agency_ready_percent || 0;
  const isReady = readyPercent >= 80;

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="executive-snapshot">
      {/* Header */}
      <div className="bg-[#0A192F] rounded-lg p-8 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="text-[#D4C19C] font-medium tracking-wide uppercase text-xs mb-2">
              Executive Snapshot
            </p>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
              {getGreeting()}
            </h1>
            <p className="text-white/60">
              {snapshot.agency_name} · {new Date(snapshot.timestamp).toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}
            </p>
          </div>
          <div className="text-left md:text-right">
            <p className="text-white/60 text-sm uppercase tracking-wide mb-1">Operational Readiness</p>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-bold text-white">{readyPercent}%</span>
              <Badge 
                className={`${isReady ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-amber-500/20 text-amber-300 border-amber-500/30"}`}
              >
                {isReady ? "Ready" : "Setup In Progress"}
              </Badge>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#D4C19C] to-[#D4C19C]/70 rounded-full transition-all duration-500"
              style={{ width: `${readyPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Core Metrics Grid */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-[#0A192F] mb-4">Today&apos;s Operations</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            icon={Heart}
            label="Active Clients"
            value={snapshot.clients_count}
            status={snapshot.clients_count > 0 ? "good" : "warning"}
          />
          <MetricCard
            icon={Users}
            label="Team Members"
            value={snapshot.caregivers_count}
            status={snapshot.caregivers_count > 0 ? "good" : "warning"}
          />
          <MetricCard
            icon={Calendar}
            label="Scheduled Today"
            value={snapshot.todays_visits}
            subtext={snapshot.visits_completed > 0 ? `${snapshot.visits_completed} completed` : null}
          />
          <MetricCard
            icon={AlertTriangle}
            label="Exceptions"
            value={snapshot.exceptions_count}
            status={snapshot.exceptions_count === 0 ? "good" : snapshot.exceptions_count <= 2 ? "warning" : "alert"}
          />
        </div>
      </div>

      {/* Status Grid */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-[#0A192F] mb-4">Operational Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ReadinessIndicator ready={snapshot.payroll_ready} label="Payroll Processing" />
          <ReadinessIndicator ready={snapshot.billing_ready} label="Billing Queue" />
          <MetricCard
            icon={Shield}
            label="Compliance Alerts"
            value={snapshot.compliance_alerts}
            status={snapshot.compliance_alerts === 0 ? "good" : "warning"}
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-[#0A192F] mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <QuickAction
            icon={AlertTriangle}
            label="Exception Center"
            href="/exceptions"
            description={`${snapshot.exceptions_count} items requiring attention`}
          />
          <QuickAction
            icon={Activity}
            label="Operational Readiness"
            href="/validation"
            description="Review readiness checklist"
          />
          <QuickAction
            icon={FileText}
            label="Pilot Journal"
            href="/pilot-journal"
            description={`${snapshot.pilot_observations} observations pending review`}
          />
          <QuickAction
            icon={Briefcase}
            label="Today's Work"
            href="/briefing"
            description="View your personalized work context"
          />
        </div>
      </div>

      {/* Extended Metrics */}
      {(snapshot.coverage_needed > 0 || snapshot.pending_payroll > 0 || snapshot.unbilled_visits > 0) && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-[#0A192F] mb-4">Attention Required</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {snapshot.coverage_needed > 0 && (
              <MetricCard
                icon={Calendar}
                label="Coverage Needed"
                value={snapshot.coverage_needed}
                status="warning"
              />
            )}
            {snapshot.pending_payroll > 0 && (
              <MetricCard
                icon={DollarSign}
                label="Pending Payroll"
                value={snapshot.pending_payroll}
                status={snapshot.pending_payroll > 10 ? "warning" : "neutral"}
              />
            )}
            {snapshot.unbilled_visits > 0 && (
              <MetricCard
                icon={Receipt}
                label="Unbilled Visits"
                value={snapshot.unbilled_visits}
                status={snapshot.unbilled_visits > 5 ? "warning" : "neutral"}
              />
            )}
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-[#E2E8F0]">
        <div className="flex items-center gap-2 text-sm text-[#475569]">
          <Clock className="w-4 h-4" />
          <span>
            Last updated: {new Date(snapshot.timestamp).toLocaleTimeString()} · {snapshot.snapshot_type}
          </span>
        </div>
        <Button
          variant="outline"
          onClick={generateSnapshot}
          disabled={generating}
          className="border-[#E2E8F0] text-[#0A192F] hover:bg-[#F1F5F9] rounded"
          data-testid="refresh-snapshot-btn"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Refresh Snapshot
        </Button>
      </div>
    </div>
  );
}
